import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface PurchaseGiftCardRequest {
  amount: number;
  purchaserName: string;
  purchaserEmail: string;
  purchaserPhone?: string;
  recipientName?: string;
  recipientEmail?: string;
  message?: string;
  isGift?: boolean;
  sendImmediately?: boolean;
  paymentToken: string;
  paymentEmail: string;
}

export interface PurchaseGiftCardResponse {
  success: boolean;
  orderNumber: string;
  giftCardCode: string;
  amount: number;
  balance: number;
}

export interface ValidateGiftCardRequest {
  code: string;
  orderAmount?: number;
}

export interface ValidateGiftCardResponse {
  valid: boolean;
  code?: string;
  currentBalance?: number;
  appliedAmount?: number;
  remainingBalance?: number;
  error?: string;
  reason?: string;
}

export interface RedeemGiftCardRequest {
  code: string;
  amountCents: number;
  orderNumber?: string;
  orderId?: string;
  eventId?: string;
}

export interface GiftCard {
  id: string;
  code: string;
  amountCents: number;
  balanceCents: number;
  status: string;
  purchaserEmail: string;
  purchaserName: string;
  purchaserPhone?: string;
  recipientEmail?: string;
  recipientName?: string;
  message?: string;
  isGift: boolean;
  sendImmediately: boolean;
  stripePaymentId?: string;
  hstCents: number;
  totalPaidCents: number;
  expiresAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface GiftCardTransaction {
  id: string;
  type: string;
  amountCents: number;
  balanceBeforeCents: number;
  balanceAfterCents: number;
  orderNumber?: string;
  orderId?: string;
  eventId?: string;
  notes?: string;
  createdAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class GiftCardsService {
  private readonly apiUrl = `${environment.apiBaseUrl}/gift-cards`;

  constructor(private http: HttpClient) {}

  /**
   * Purchase a new gift card
   */
  purchaseGiftCard(request: PurchaseGiftCardRequest): Observable<PurchaseGiftCardResponse> {
    return this.http.post<any>(`${this.apiUrl}/purchase`, request).pipe(
      map(response => {
        // Backend returns ApiResponseDto { data, statusCode, message }
        const data = response.data || response;
        return {
          success: true,
          orderNumber: data.id,
          giftCardCode: data.code,
          amount: data.amountCents / 100,
          balance: data.balanceCents / 100,
        };
      })
    );
  }

  /**
   * Validate a gift card for redemption
   */
  validateGiftCard(request: ValidateGiftCardRequest): Observable<ValidateGiftCardResponse> {
    return this.http.post<ValidateGiftCardResponse>(`${this.apiUrl}/validate`, request);
  }

  /**
   * Redeem a gift card
   */
  redeemGiftCard(request: RedeemGiftCardRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/redeem`, request);
  }

  /**
   * Get gift card details by code
   */
  getGiftCardDetails(code: string): Observable<GiftCard> {
    return this.http.get<any>(`${this.apiUrl}/verify/${code}`).pipe(
      map(response => response.data || response)
    );
  }
}
